/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/constants';
import { ISpecificPageTriggerContext, TContextProvider } from '../../../interfaces';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * SpecificPageTrigger class.
 * Proactive chat is triggered if current page is listed in config.proactiveOnSpecificPageList.
 */
export default class SpecificPageTrigger extends ProactiveChatTrigger {
    private specificPageName: string = '';

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: current page is listed in the proactive chat list of pages.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        let isValid = false;
        if (this.props.config.proactiveOnSpecificPageList) {
            this.props.context.telemetry.debug(
                `[${SpecificPageTrigger.name}] Number of items in user-defined list of 'Specific Pages' was '${this.props.config.proactiveOnSpecificPageList?.length}'.`
            );

            for (let i = 0; i < (this.props.config.proactiveOnSpecificPageList?.length || 0); i++) {
                const specificPageTrimmed: string = this.props.config.proactiveOnSpecificPageList[i].trim();

                this.props.context.telemetry.debug(`[] Current location URL: ${window.location.href}.`);
                this.props.context.telemetry.debug(`[] Current item from user-defined list of 'Specific Page': ${specificPageTrimmed}.`);

                if (window.location.href.indexOf(specificPageTrimmed) > -1) {
                    this.specificPageName = specificPageTrimmed;
                    isValid = true;
                    break;
                }
            }
        } else {
            this.props.context.telemetry.debug(`[Chat][${SpecificPageTrigger.name}] User-defined 'Specific Page' list was undefined.`);
        }

        this.props.context.telemetry.debug(`[Chat][${SpecificPageTrigger.name}] Validation returning '${isValid}'.`);
        return isValid;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): ISpecificPageTriggerContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.SpecificPage,
                    isDisplayable: true
                },
                ProactivePage: {
                    value: this.specificPageName,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnSpecificPageMessage || this.props.config.proactiveDefaultMessage;
    }
}
